import { Box, Button, Typography, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { isCoordinatesValid } from '@terragotech/gen5-shared-utilities';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState, useContext } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ActionIcon from '../../ActionIcon';
import { BaseLocationMapProps } from './CommonEditorUtils';
import TGCoordinateTextField from './TGCoordinateTextField';
import { colors } from '../../../styles/theme';
import { MOBILE_BREAKPOINT } from '../../../utils/utilityHelper';
import { LanguageContext } from '../../../contexts/LanguageContext/languageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FloorPlanType } from '../../../contexts/AggregatesContext/types';
import { useFloorPlanSection } from '../../../hooks/sectionsHooks/useFloorPlanSection';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useAlert } from '../../../contexts/AlertModalContext';

interface TGMultipleLocationSidebarProps extends BaseLocationMapProps {
  value: GeoJSON.Point | GeoJSON.Point[] | GeoJSON.LineString | null | undefined;
  capturedLocation: GeoJSON.Point | GeoJSON.Point[] | null | undefined;
  setCapturedLocation: Dispatch<SetStateAction<GeoJSON.Point | GeoJSON.Point[] | null | undefined>>;
  setManualMode: Dispatch<SetStateAction<boolean>>;
  maximum: number | undefined;
  unlimited: boolean | undefined;
  selectedPoint:
    | {
        coordinates: number[];
        indexOfCoords: number;
      }
    | undefined;
  setSelectedPoint: React.Dispatch<
    React.SetStateAction<
      | {
          coordinates: number[];
          indexOfCoords: number;
        }
      | undefined
    >
  >;
  selectedPointRef: React.MutableRefObject<
    | {
        coordinates: number[];
        indexOfCoords: number;
      }
    | undefined
  >;
  setOpenAddLocationAlert: React.Dispatch<React.SetStateAction<boolean>>;
  isFloorPlanLocation?: boolean;
  selectedFloorPlan?: FloorPlanType | null;
  setSelectedFloorPlan?: Dispatch<SetStateAction<FloorPlanType | null>>;
  children?: React.ReactElement;
}
export const PADDING_SPACE = 49;
const HEADER_FOOTER_HEIGHT = 127;
export const CONTAINER_HEIGHT_MOB = 166;
const useStyles = makeStyles(theme => ({
  text: {
    fontFamily: 'Roboto',
    fontSize: '15px',
    padding: 0,
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '0em',
    fontStyle: 'normal',
    '&.MuiButton-root.Mui-disabled': {
      color: colors.black35,
    },
  },
  addLocationIcon: {
    '&.disabled': {
      filter: 'sepia(0%) saturate(5424%) hue-rotate(322deg) brightness(136%)',
    },
  },
  rootContainer: {
    height: `calc(100% - ${PADDING_SPACE}px)`,
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      height: `100%`,
    },
  },
  mainContainer: {
    padding: '22px 30px',
    height: `calc(100% - ${HEADER_FOOTER_HEIGHT}px)`,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 7,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 5,
      backgroundColor: colors.scrollBar,
    },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      padding: '22px',
      paddingTop: 20,
      height: `100%`,
    },
    maxHeight: `calc(100vh - ${HEADER_FOOTER_HEIGHT}px - ${PADDING_SPACE}px - 50px)`,
  },
  floorPlanInputs: {
    border: `1px solid ${colors.black10}`,
    borderRadius: 8,
    backgroundColor: colors.white,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '9px 16px',
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      padding: '10px 16px',
    },
  },
  floorPlanSelectorContainer: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  txt: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 'normal',
    color: colors.black0,
  },
  badge: {
    backgroundColor: colors.ghostWhite,
    border: `1px solid ${colors.black10}`,
    borderRadius: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3px 10px',
  },
  badgeTxt: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  inputContainer: {
    paddingBottom: 10,
  },
  icon: {
    paddingLeft: 10,
    color: theme.palette.primary.main,
  },
  floorPlanSelector: {
    border: `1px solid ${colors.black10}`,
    borderRadius: 8,
    backgroundColor: colors.white,
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      padding: '10px 16px',
    },
  },
  floorPlanTxt: {
    color: theme.palette.primary.main,
  },
  inputFieldsContainerRoot2: {
    padding: '22px 30px',
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      padding: '20px 22px',
    },
  },
  floorPlanContainer: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${colors.black10}`,
    borderRadius: 8,
    backgroundColor: colors.white,
    overflowY: 'auto',
    maxHeight: `calc(100vh - ${HEADER_FOOTER_HEIGHT}px - ${PADDING_SPACE}px - 150px)`,
  }, 
  bottomBorder: {
    borderBottom: `1px solid ${colors.black10}`,
    padding: '10px 16px',
  },
  floorPlanCoordinate: {
    padding: '10px 16px',
  },
}));

const TGMultipleLocationSidebar = (props: TGMultipleLocationSidebarProps) => {
  const {
    maximum,
    unlimited,
    capturedLocation,
    setCapturedLocation,
    setIsValidLocation,
    selectedPoint,
    setSelectedPoint,
    selectedPointRef,
    setOpenAddLocationAlert,
    isFloorPlanLocation,
    selectedFloorPlan,
    setSelectedFloorPlan,
    children,
  } = props;
  const classes = useStyles();
  const [blankField, setBlankField] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const theme = useTheme();
  const { openConfirmation } = useAlert();
  const { translate } = useContext(LanguageContext);
  const [isPointSelected, setIsPointSelected] = useState<boolean>(!!capturedLocation);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const onFloorPlanClick = async (floorPlan: FloorPlanType | null) => {
    if (isFloorPlanLocation) {
      if (isPointSelected) {
        const status = await openConfirmation({
          title: 'Are you sure?',
          question: 'Changing the floor plan will clear any selected location points. Do you want to continue?',
          confirmationText: 'Continue',
        });
        if (status !== 'confirm') {
          return;
        }
        setCapturedLocation && setCapturedLocation([]);
      }
      setIsOpen(!isOpen);
      setSelectedFloorPlan && setSelectedFloorPlan(floorPlan);
    }
  }
  const { section: floorPlanSection } = useFloorPlanSection(selectedFloorPlan, setSelectedFloorPlan, onFloorPlanClick);

  useEffect(() => {
    if (capturedLocation) {
      if (Array.isArray(capturedLocation) && capturedLocation.length === 0) {
        setIsPointSelected(false);
      } else {
        setIsPointSelected(true);
      }
    }
  }, [capturedLocation]);

  const onClick = () => {
    if (isFloorPlanLocation) {
      setIsOpen(!isOpen);
    }
  };
  
  useEffect(() => {
    if (Array.isArray(capturedLocation)) {
      let invalidLocation = capturedLocation.some(
        (location: GeoJSON.Point) =>
          !location ||
          !isCoordinatesValid(location.coordinates[1], location.coordinates[0]) ||
          location.type !== 'Point'
      );
      if (invalidLocation) {
        setIsValidLocation(false);
      } else {
        setIsValidLocation(capturedLocation.length > 0);
      }
    }
  }, [capturedLocation, blankField]);

  const canCaptureLocation = useMemo(() => {
    // Location must be array or null or undefined
    return (capturedLocation && Array.isArray(capturedLocation)) || !capturedLocation;
  }, [capturedLocation]);

  const canAddLocation = useMemo(() => {
    // If maximum exists, and capturedLocation is not null or undefined, the length must be less than the maximum. Unless unlimited is true
    const maxCheck =
      (!!maximum && Array.isArray(capturedLocation) && (capturedLocation || []).length < maximum) ||
      !capturedLocation ||
      unlimited;
    return canCaptureLocation && maxCheck;
  }, [canCaptureLocation, capturedLocation, maximum, unlimited]);

  useEffect(() => {
    if (!canAddLocation) {
      setBlankField(false);
    } else {
      setBlankField(true);
    }
  }, [blankField, canAddLocation]);

  const calculatedHeight = useMemo(() => {
    if (capturedLocation && Array.isArray(capturedLocation)) {
      return capturedLocation.length * 38;
    } else {
      return 0;
    }
  }, [capturedLocation]);

  return (
    <div className={classes.rootContainer}>
      {children}
      <div className={isFloorPlanLocation ? classes.inputFieldsContainerRoot2 : ''}>
        {isFloorPlanLocation &&
          <>
            <Box className={classes.inputContainer}>
              <Box className={`${isOpen ? classes.floorPlanSelectorContainer : ''} ${classes.floorPlanInputs}`}>
                <Typography className={classes.txt}>{translate('Floor Plan')}</Typography>
                <Box onClick={onClick} className={classes.badge}>
                  <Typography className={`${classes.badgeTxt} ${classes.floorPlanTxt}`}>{selectedFloorPlan ? selectedFloorPlan.title : translate('Unselected')}</Typography>
                  <FontAwesomeIcon icon={(isOpen ? faCaretUp : faCaretDown) as IconProp} className={classes.icon} />
                </Box>
              </Box>
              {isOpen && 
                <Box style={{ paddingTop: 5 }}>
                  <Box className={classes.floorPlanSelector}>
                    {floorPlanSection.content}
                  </Box>
                </Box>
              }
            </Box>
            {!isOpen && 
              <Box className={classes.inputContainer}>
                <Box className={classes.floorPlanInputs}>
                  <Typography className={classes.txt}>{translate('Location Status')}</Typography>
                  <Box className={classes.badge}>
                    <Typography className={classes.badgeTxt}>{translate(isPointSelected ? 'Selected' : 'Unselected')}</Typography>
                  </Box>
                </Box>
              </Box>
            }
          </>
        }
        {!isOpen && 
          <div className={isFloorPlanLocation ? '' : classes.mainContainer}>
            <Box className={isFloorPlanLocation ? classes.floorPlanContainer : ''}>
              {
                <DragDropContext
                  onDragStart={() => {
                    setIsDragging(true);
                  }}
                  onDragEnd={(result, provided) => {
                    setIsDragging(false);
                    if (!result.destination) {
                      return;
                    }
                    let temp;
                    if (!Array.isArray(capturedLocation) && capturedLocation) {
                      temp = [capturedLocation];
                    } else {
                      temp = capturedLocation;
                    }
                    if (!temp) return;
                    const newItems = [...temp];
                    const [removed] = newItems.splice(result.source.index, 1);
                    newItems.splice(result.destination.index, 0, removed);
                    setCapturedLocation(newItems);
                  }}
                >
                  <Droppable droppableId="uniqueDroppableId">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} style={{ minHeight: calculatedHeight }}>
                        {capturedLocation &&
                          Array.isArray(capturedLocation) &&
                          (capturedLocation || []).map((x: GeoJSON.Point, index: number) => {
                            return (
                              <Draggable index={index} draggableId={String(index)} key={index}>
                                {(provided, snapshot) => (
                                  <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                    <Box
                                      className={isFloorPlanLocation ? `${classes.floorPlanCoordinate} ${
                                      canAddLocation || index !== capturedLocation.length - 1 ? classes.bottomBorder : ''
                                      }` : ''}
                                    >
                                      <TGCoordinateTextField
                                        index={index}
                                        capturedLocation={capturedLocation}
                                        setCapturedLocation={setCapturedLocation}
                                        specificLocation={x}
                                        setBlankField={setBlankField}
                                        selectedPoint={selectedPoint}
                                        setSelectedPoint={setSelectedPoint}
                                        selectedPointRef={selectedPointRef}
                                        isFloorPlanLocation={isFloorPlanLocation}
                                      />
                                    </Box>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })
                        }
                        {blankField && canAddLocation && (
                          <Box className={isFloorPlanLocation ? classes.floorPlanCoordinate : ''}>
                            <TGCoordinateTextField
                              index={Array.isArray(capturedLocation) ? capturedLocation.length : 0}
                              capturedLocation={capturedLocation}
                              setCapturedLocation={setCapturedLocation}
                              specificLocation={undefined}
                              blankField={blankField}
                              setBlankField={setBlankField}
                              setSelectedPoint={setSelectedPoint}
                              selectedPointRef={selectedPointRef}
                              isFloorPlanLocation={isFloorPlanLocation}
                            />
                          </Box>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              }
              {isDragging === false && !isFloorPlanLocation && (
                <div style={{ marginTop: 18 }}>
                  <Button
                    className={classes.text}
                    color="primary"
                    aria-label="Add Location"
                    disabled={blankField || !canCaptureLocation}
                    onClick={() => {
                      if (canAddLocation) {
                        setBlankField(true);
                      } else {
                        setOpenAddLocationAlert(true);
                      }
                    }}
                  >
                    <ActionIcon name="fa-circle-plus" color={(blankField || !canCaptureLocation) ? colors.black25 : theme.palette.primary.main} />{' '}
                    <span style={{ paddingLeft: 8, textTransform: 'none' }}>{translate('Add Location')}</span>
                  </Button>
                </div>
              )}
            </Box>
          </div>
        }
      </div>
    </div>
  );
};

export default TGMultipleLocationSidebar;
